import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { ChatService } from 'lib-trend-core';
import { of } from 'rxjs';
import { catchError, filter, map, switchMap, withLatestFrom } from 'rxjs/operators';
import * as ChatActions from './chat.actions';
import { conversationsSelector } from './chat.selectors';

/**
 * Internal Chat store effects (SPEC-2026-07-internal-chat, T-18) — REST reads
 * only (ChatService). All writes go through the websocket command path
 * (ChatSocketService) and come back as socket fan-out bridged by
 * ChatSocketBridgeService — never through HTTP effects.
 */
@Injectable()
export class ChatEffects {

  constructor(
    private readonly actions$: Actions,
    private readonly store: Store,
    private readonly chatService: ChatService,
  ) { }

  loadBootstrapEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ChatActions.loadBootstrap),
      switchMap(() =>
        this.chatService.getBootstrap().pipe(
          map((envelope) => ChatActions.setBootstrap({ data: envelope?.data })),
          // `status` lets the reducer classify unreachable-backend (transport /
          // 5xx / 404) vs a refused request — see ChatBootstrapErrorKind (T-25).
          catchError((error) => of(ChatActions.setBootstrapFailure({
            error: error?.message ?? 'bootstrap failed',
            status: typeof error?.status === 'number' ? error.status : 0,
          }))),
        ),
      ),
    );
  });

  discoverEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ChatActions.discoverChannels),
      switchMap((action) =>
        this.chatService.discoverChannels({ search: action.search, page: action.page, perPage: action.perPage }).pipe(
          map((envelope) => ChatActions.setDiscoveredChannels({
            channels: envelope?.data ?? [],
            meta: envelope?.meta,
            append: action.append,
          })),
          catchError((error) => of(ChatActions.setFailure({ error: error?.message ?? 'discover failed' }))),
        ),
      ),
    );
  });

  savedEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ChatActions.loadSaved),
      switchMap(() =>
        this.chatService.getSaved().pipe(
          map((envelope) => ChatActions.setSaved({
            saved: envelope?.data ?? [],
            userDisplay: envelope?.meta?.userDisplay,
          })),
          catchError((error) => of(ChatActions.setFailure({ error: error?.message ?? 'saved failed' }))),
        ),
      ),
    );
  });

  /**
   * Channel lifecycle fan-out that can change MY sidebar dataset (created /
   * invited / joined) carries no full channel document — re-bootstrap to pick
   * up the new conversation, roster and membership. `loadBootstrapEffect$`
   * uses switchMap, so bursts collapse into the latest request.
   */
  refreshOnChannelUpdateEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ChatActions.applyChannelUpdate),
      filter((action) => ['created', 'invited', 'joined'].includes(action.update?.action)),
      map(() => ChatActions.loadBootstrap()),
    );
  });

  /**
   * Unread fan-out for a conversation the snapshot does not know yet (e.g. a
   * DM just created by a teammate) — re-bootstrap to pick it up.
   */
  refreshOnUnknownUnreadEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ChatActions.applyUnreadUpdate),
      withLatestFrom(this.store.select(conversationsSelector)),
      filter(([action, conversations]) =>
        action.update?.preview !== undefined
        && !!action.update?.channelId
        && !conversations.some((conversation) => conversation._id === action.update.channelId)),
      map(() => ChatActions.loadBootstrap()),
    );
  });
}
